import { useState } from "react";
import { CheckCircle2, Pencil, Plus, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";

import { DatePicker, fromIsoDate, toIsoDate } from "@/components/cinch/DatePicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useClosers } from "@/features/contracts/api";
import { useUsers } from "@/lib/rbac";

import {
  ARCHETYPE_LABEL,
  ROLE_LABEL,
  fmtMoney2,
  fmtRate,
  useApprovals,
  useApproveAssignment,
  useApprovePlan,
  useCreatePlan,
  useDeletePayee,
  useDeletePlan,
  usePayees,
  usePlans,
  useSavePayee,
  useSetPlanRate,
  type Archetype,
  type CompPlan,
  type Payee,
  type Role,
} from "../api";
import { useCan } from "@/lib/auth";

export function AdminView() {
  const approvals = useApprovals();
  const pending = approvals.data?.length ?? 0;
  return (
    <Tabs defaultValue="payees">
      <TabsList>
        <TabsTrigger value="payees">Payees</TabsTrigger>
        <TabsTrigger value="plan">Comp Plan</TabsTrigger>
        <TabsTrigger value="approvals">
          Approvals{pending > 0 ? ` (${pending})` : ""}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="payees" className="mt-4">
        <PayeeRoster />
      </TabsContent>
      <TabsContent value="plan" className="mt-4">
        <PlanView />
      </TabsContent>
      <TabsContent value="approvals" className="mt-4">
        <ApprovalsQueue />
      </TabsContent>
    </Tabs>
  );
}

// ── payee roster ────────────────────────────────────────────────────────────

function PayeeRoster() {
  const { data, isLoading } = usePayees();
  const del = useDeletePayee();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Payee | null>(null);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Everyone who can be paid commission — employees (linked to their login) and
          referral partners.
        </p>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Add payee
        </Button>
      </div>

      {isLoading && !data && <Card className="h-40 animate-pulse" />}

      {data && data.length === 0 && (
        <Card className="flex flex-col items-center gap-2 p-10 text-center">
          <UserRound className="h-8 w-8 text-muted-foreground" />
          <p className="font-medium">No payees yet</p>
          <p className="max-w-md text-sm text-muted-foreground">
            Add the commissioned people first — then assign them to contracts from the
            Contract Ledger tab.
          </p>
        </Card>
      )}

      {data && data.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Login link</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Assignments</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.display_name}</TableCell>
                    <TableCell>{ROLE_LABEL[p.default_role]}</TableCell>
                    <TableCell>
                      {p.user_email ?? (
                        <span className="text-muted-foreground">not linked</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {p.is_partner ? (
                        <Badge variant="info">Partner</Badge>
                      ) : (
                        <Badge variant="secondary">Employee</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {p.assignment_count}
                    </TableCell>
                    <TableCell>
                      {p.is_active ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="neutral">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Edit ${p.display_name}`}
                        onClick={() => {
                          setEditing(p);
                          setEditorOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Delete ${p.display_name}`}
                        disabled={p.assignment_count > 0}
                        title={
                          p.assignment_count > 0
                            ? "In use — deactivate instead so history stays intact"
                            : undefined
                        }
                        onClick={() =>
                          del.mutate(p.id, {
                            onSuccess: () => toast.success(`${p.display_name} deleted`),
                            onError: (e) =>
                              toast.error("Can't delete", {
                                description: (e as Error).message,
                              }),
                          })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <PayeeDialog open={editorOpen} onOpenChange={setEditorOpen} existing={editing} />
    </div>
  );
}

function PayeeDialog({
  open,
  onOpenChange,
  existing,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  existing: Payee | null;
}) {
  const save = useSavePayee();
  const { data: users } = useUsers();
  const closers = useClosers();
  const [name, setName] = useState("");
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<Role>("ae");
  const [isPartner, setIsPartner] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [hubspotId, setHubspotId] = useState("");
  const [notes, setNotes] = useState("");

  // Reset per open (uncontrolled-dialog pattern kept simple).
  const [seeded, setSeeded] = useState<string | null>(null);
  const seedKey = existing?.id ?? "new";
  if (open && seeded !== seedKey) {
    setSeeded(seedKey);
    setName(existing?.display_name ?? "");
    setUserId(existing?.user_id ?? "");
    setRole(existing?.default_role ?? "ae");
    setIsPartner(existing?.is_partner ?? false);
    setIsActive(existing?.is_active ?? true);
    setHubspotId(existing?.hubspot_owner_id ?? "");
    setNotes(existing?.notes ?? "");
  }
  if (!open && seeded !== null) setSeeded(null);

  // Employees must be a Cinch Ops user (that link drives "see only my
  // commissions" for read users). Sort known closers first — they're the
  // most likely payees — using the same closers list proposals uses.
  const closersByEmail = new Map(
    (closers.data?.results ?? [])
      .filter((c) => c.email)
      .map((c) => [c.email!.toLowerCase(), c] as const),
  );
  const userOptions = (users ?? [])
    .filter((u) => u.is_active || u.id === userId)
    .map((u) => ({ ...u, closer: closersByEmail.get(u.email.toLowerCase()) }))
    .sort((a, b) =>
      !a.closer === !b.closer ? a.name.localeCompare(b.name) : a.closer ? -1 : 1,
    );
  const selectedUser = userOptions.find((u) => u.id === userId);
  const effectiveName = isPartner ? name : (selectedUser?.name ?? "");
  // The selected user matches a HubSpot-backed closer → lock in their HubSpot
  // owner id automatically so attribution stays clean everywhere.
  const autoHubspotId =
    !isPartner && selectedUser?.closer?.hubspot_owner_id
      ? String(selectedUser.closer.hubspot_owner_id)
      : null;
  const effectiveHubspotId = autoHubspotId ?? hubspotId;

  const submit = () => {
    save.mutate(
      {
        id: existing?.id,
        body: {
          display_name: effectiveName.trim(),
          default_role: role,
          user_id: isPartner ? null : userId || null,
          clear_user: isPartner,
          is_partner: isPartner,
          is_active: isActive,
          hubspot_owner_id: effectiveHubspotId.trim() || null,
          notes: notes.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success(existing ? "Payee updated" : "Payee added");
          onOpenChange(false);
        },
        onError: (e) => toast.error("Couldn't save", { description: (e as Error).message }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit payee" : "Add payee"}</DialogTitle>
          <DialogDescription>
            Employees are picked from Cinch Ops users — that login link is what
            lets a rep see their own commissions. Ad hoc names are for referral
            partners (no login).
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="pd-partner">Referral partner (non-employee)</Label>
            <Switch id="pd-partner" checked={isPartner} onCheckedChange={setIsPartner} />
          </div>
          {isPartner ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pd-name">Partner name</Label>
              <Input
                id="pd-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Repair Shops Websites (RP)"
              />
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pd-user">Cinch Ops user</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger id="pd-user">
                  <SelectValue placeholder="Pick an employee" />
                </SelectTrigger>
                <SelectContent>
                  {userOptions.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}
                      {u.closer ? " · closer" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Known closers (from the proposals closer list) sort first. The
                payee's display name comes from the user record
                {selectedUser ? ` — "${selectedUser.name}"` : ""}.
              </p>
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pd-role">Default role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as Role)}>
              <SelectTrigger id="pd-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABEL).map(([k, label]) => (
                  <SelectItem key={k} value={k}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pd-hs">
              HubSpot owner ID {autoHubspotId ? "(from the closer list)" : "(optional)"}
            </Label>
            <Input
              id="pd-hs"
              value={effectiveHubspotId}
              onChange={(e) => setHubspotId(e.target.value)}
              disabled={autoHubspotId != null}
            />
            {autoHubspotId != null && (
              <p className="text-xs text-muted-foreground">
                Locked automatically — this user matches a HubSpot closer.
              </p>
            )}
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="pd-active">Active (selectable on new assignments)</Label>
            <Switch id="pd-active" checked={isActive} onCheckedChange={setIsActive} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pd-notes">Notes</Label>
            <Input id="pd-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!effectiveName.trim() || save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── comp plans (named, dated, approved as a whole) ──────────────────────────

const PLAN_ROLES: Role[] = [
  "ae",
  "bdr",
  "sales_engineer",
  "cro",
  "referral_partner",
  "partner_manager",
  "csm",
  "product",
];
const PLAN_ARCHETYPES: Archetype[] = [
  "new",
  "plg",
  "upsell",
  "replacement",
  "reseller",
  "po",
  "referral",
];

function PlanView() {
  const { data: plans, isLoading } = usePlans();
  const approve = useApprovePlan();
  const del = useDeletePlan();
  const canApprove = useCan("commissions.plan_approve");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [cell, setCell] = useState<{ role: Role; archetype: Archetype } | null>(null);

  const current = (plans ?? []).find((p) => p.is_current) ?? null;
  const plan =
    (plans ?? []).find((p) => p.id === selectedId) ?? current ?? (plans ?? [])[0] ?? null;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        The comp plan is ONE named, dated document — the whole role × contract-type
        grid, approved as a unit by the CFO/CEO. Per-deal exceptions stay on the
        assignment (off-plan approval); the plan itself only changes by version.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Plan
          </span>
          <Select value={plan?.id ?? ""} onValueChange={setSelectedId}>
            <SelectTrigger className="h-9 w-[300px]">
              <SelectValue placeholder="No plans yet" />
            </SelectTrigger>
            <SelectContent>
              {(plans ?? []).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} — {p.effective_from}
                  {p.status === "pending_approval"
                    ? " (pending)"
                    : p.is_current
                      ? " (current)"
                      : p.effective_to
                        ? ` (until ${p.effective_to})`
                        : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {plan &&
            (plan.status === "pending_approval" ? (
              <Badge variant="warning">Awaiting CFO/CEO approval</Badge>
            ) : plan.is_current ? (
              <Badge variant="success">Current plan</Badge>
            ) : (
              <Badge variant="neutral">
                {plan.effective_to ? `Past — until ${plan.effective_to}` : "Future"}
              </Badge>
            ))}
        </div>
        <div className="flex items-center gap-2">
          {plan && plan.status === "pending_approval" && canApprove && (
            <Button
              size="sm"
              disabled={approve.isPending}
              onClick={() =>
                approve.mutate(plan.id, {
                  onSuccess: () => toast.success(`"${plan.name}" approved`),
                  onError: (e) =>
                    toast.error("Couldn't approve", { description: (e as Error).message }),
                })
              }
            >
              <CheckCircle2 className="mr-1.5 h-4 w-4" />
              Approve plan
            </Button>
          )}
          {plan && plan.status === "pending_approval" && (
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Delete draft ${plan.name}`}
              disabled={del.isPending}
              onClick={() =>
                del.mutate(plan.id, {
                  onSuccess: () => {
                    toast.success("Draft deleted");
                    setSelectedId(null);
                  },
                  onError: (e) =>
                    toast.error("Can't delete", { description: (e as Error).message }),
                })
              }
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            New plan
          </Button>
        </div>
      </div>

      {plan?.approved_by_name && plan.status === "approved" && (
        <p className="text-xs text-muted-foreground">
          Approved by {plan.approved_by_name}
          {plan.approved_at ? ` on ${new Date(plan.approved_at).toLocaleDateString()}` : ""}.
          Editing any cell resets the plan to pending and needs a fresh approval.
        </p>
      )}

      {isLoading && !plans && <Card className="h-56 animate-pulse" />}

      {plans && plans.length === 0 && (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No comp plan yet — create the first one (e.g. "2026 Comp Plan"), fill in the
          grid, and have the CFO/CEO approve it.
        </Card>
      )}

      {plan && (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role</TableHead>
                  {PLAN_ARCHETYPES.map((a) => (
                    <TableHead key={a} className="text-center">
                      {ARCHETYPE_LABEL[a]}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {PLAN_ROLES.map((role) => (
                  <TableRow key={role}>
                    <TableCell className="font-medium">{ROLE_LABEL[role]}</TableCell>
                    {PLAN_ARCHETYPES.map((a) => {
                      const rate = plan.rates.find(
                        (r) => r.role === role && r.contract_archetype === a,
                      );
                      return (
                        <TableCell key={a} className="text-center">
                          <button
                            type="button"
                            className="min-w-[56px] rounded border border-dashed border-transparent px-2 py-1 tabular-nums transition-colors hover:border-border hover:bg-muted"
                            onClick={() => setCell({ role, archetype: a })}
                            aria-label={`Edit ${ROLE_LABEL[role]} rate for ${ARCHETYPE_LABEL[a]}`}
                          >
                            {rate ? (
                              <span className="font-medium">{fmtRate(rate.rate_pct)}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </button>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <PlanCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        copyFrom={current ?? plan}
        onCreated={(id) => setSelectedId(id)}
      />

      {cell && plan && (
        <PlanCellDialog
          plan={plan}
          role={cell.role}
          archetype={cell.archetype}
          onClose={() => setCell(null)}
        />
      )}
    </div>
  );
}

function PlanCreateDialog({
  open,
  onOpenChange,
  copyFrom,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  copyFrom: CompPlan | null;
  onCreated: (id: string) => void;
}) {
  const create = useCreatePlan();
  const [name, setName] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [copy, setCopy] = useState(true);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New comp plan</DialogTitle>
          <DialogDescription>
            A named version of the whole grid with its own effective date. It becomes
            the plan once the CFO/CEO approves it — approval also closes the previous
            plan the day before this one starts.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pc-name">Plan name</Label>
            <Input
              id="pc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`e.g. ${new Date().getFullYear()} Comp Plan`}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pc-from">Effective from</Label>
            <DatePicker
              id="pc-from"
              aria-label="Effective from"
              placeholder="Pick a date"
              value={fromIsoDate(effectiveFrom)}
              onChange={(d) => setEffectiveFrom(d ? toIsoDate(d) : "")}
            />
          </div>
          {copyFrom && (
            <div className="flex items-center justify-between">
              <Label htmlFor="pc-copy">Start from "{copyFrom.name}"</Label>
              <Switch id="pc-copy" checked={copy} onCheckedChange={setCopy} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || !effectiveFrom || create.isPending}
            onClick={() =>
              create.mutate(
                {
                  name: name.trim(),
                  effective_from: effectiveFrom,
                  copy_from_plan_id: copy && copyFrom ? copyFrom.id : null,
                },
                {
                  onSuccess: (p) => {
                    toast.success(`"${p.name}" created — awaiting CFO/CEO approval`);
                    onCreated(p.id);
                    onOpenChange(false);
                    setName("");
                    setEffectiveFrom("");
                  },
                  onError: (e) =>
                    toast.error("Couldn't create", { description: (e as Error).message }),
                },
              )
            }
          >
            {create.isPending ? "Creating…" : "Create plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlanCellDialog({
  plan,
  role,
  archetype,
  onClose,
}: {
  plan: CompPlan;
  role: Role;
  archetype: Archetype;
  onClose: () => void;
}) {
  const setRate = useSetPlanRate();
  const existing = plan.rates.find(
    (r) => r.role === role && r.contract_archetype === archetype,
  );
  const [rate, setRateInput] = useState(
    existing ? String(existing.rate_pct * 100) : "",
  );
  const [windowMonths, setWindowMonths] = useState(String(existing?.window_months ?? 12));

  const rateNum = Number(rate);
  const rateError =
    rate === "" || Number.isNaN(rateNum)
      ? "Rate is required"
      : rateNum < 0
        ? "Rate can't be negative"
        : rateNum > 100
          ? "Rate can't exceed 100%"
          : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {ROLE_LABEL[role]} · {ARCHETYPE_LABEL[archetype]}
          </DialogTitle>
          <DialogDescription>
            One cell of "{plan.name}".
            {plan.status === "approved"
              ? " This plan is approved — saving a change resets it to pending and needs a fresh CFO/CEO approval."
              : " Takes effect with the plan's approval."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pr-rate">Rate (%)</Label>
            <Input
              id="pr-rate"
              inputMode="decimal"
              value={rate}
              onChange={(e) => setRateInput(e.target.value)}
              aria-invalid={!!rateError}
              placeholder="15"
            />
            {rateError && rate !== "" && <p className="text-xs text-danger">{rateError}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pr-window">Window (months)</Label>
            <Input
              id="pr-window"
              inputMode="numeric"
              value={windowMonths}
              onChange={(e) => setWindowMonths(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!!rateError || setRate.isPending}
            onClick={() =>
              setRate.mutate(
                {
                  planId: plan.id,
                  body: {
                    role,
                    contract_archetype: archetype,
                    rate_pct: rateNum / 100,
                    window_months: Number(windowMonths) || 12,
                  },
                },
                {
                  onSuccess: () => {
                    toast.success("Rate saved");
                    onClose();
                  },
                  onError: (e) =>
                    toast.error("Couldn't save", { description: (e as Error).message }),
                },
              )
            }
          >
            {setRate.isPending ? "Saving…" : "Save rate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ── approvals queue ─────────────────────────────────────────────────────────

function ApprovalsQueue() {
  const { data, isLoading } = useApprovals();
  const approve = useApproveAssignment();

  return (
    <div className="flex flex-col gap-3">
      {isLoading && !data && <Card className="h-32 animate-pulse" />}

      {data && data.length === 0 && (
        <Card className="flex flex-col items-center gap-2 p-10 text-center">
          <CheckCircle2 className="h-8 w-8 text-muted-foreground" />
          <p className="font-medium">Nothing pending</p>
          <p className="max-w-md text-sm text-muted-foreground">
            Off-plan rates (a deal rate that differs from the comp plan) land here for
            approval before they pay.
          </p>
        </Card>
      )}

      {data && data.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payee</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Contract</TableHead>
                  <TableHead className="text-right">Requested</TableHead>
                  <TableHead className="text-right">Plan</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.payee_name}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {a.company_name ?? "—"}
                    </TableCell>
                    <TableCell className="max-w-[140px] truncate tabular-nums">
                      {a.contract_id}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {a.fixed_monthly_amount != null
                        ? `${fmtMoney2(a.fixed_monthly_amount)}/mo`
                        : fmtRate(a.rate_pct)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtRate(a.plan_rate_pct)}
                    </TableCell>
                    <TableCell className="max-w-[220px] truncate" title={a.notes ?? ""}>
                      {a.notes ?? "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        disabled={approve.isPending}
                        onClick={() =>
                          approve.mutate(a.id, {
                            onSuccess: () => toast.success("Approved"),
                            onError: (e) =>
                              toast.error("Couldn't approve", {
                                description: (e as Error).message,
                              }),
                          })
                        }
                      >
                        Approve
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
